import devLogger from 'react-utils/devLogger';
import { getDefaultTheme } from '../components/ThemeProvider/ThemeProvider.helpers';
import { emitFoundationsReadinessTracking } from './tracking';

/**
 * Backfills any top-level token maps that are absent from the provided theme
 * using the build-time default theme.
 *
 * A theme resolved from a deployed asset or the `window` global can predate
 * top-level token maps (e.g. `fontWeight`/`fontSize`) that newer
 * foundations-components read. Without this guard, accessing such a map throws
 * `Cannot read properties of undefined`. Only entirely-missing keys are filled
 * in, so an up-to-date theme is returned untouched (and by reference).
 */
const backfillMissingTokenMaps = theme => {
  const defaultTheme = getDefaultTheme();
  let result = theme;
  Object.keys(defaultTheme).forEach(key => {
    if (result[key] === undefined) {
      result = Object.assign({}, result, {
        [key]: defaultTheme[key]
      });
    }
  });
  return result;
};

/**
 * An internal function that provides a default theme if the theme
 * is missing or empty. This function is used to ensure that a
 * theme is always available.
 *
 * ```tsx
 * getTheme({ theme: trellisNextTheme })
 * ```
 *
 * @param options - The options for the theme accessor.
 *
 */
export const getTheme = ({
  theme
}) => {
  const isThemeMissing = !theme || Object.keys(theme).length === 0;
  emitFoundationsReadinessTracking(isThemeMissing);
  if (isThemeMissing) {
    devLogger.warn({
      message: 'Failed to load theme. No AppProvider or empty theme found.',
      key: 'foundations-theming:getTheme:theme-load-failure'
    });
    return getDefaultTheme();
  }
  return backfillMissingTokenMaps(theme);
};